import React, { useState, useEffect } from 'react';
import { PublicHeader, PublicFooter } from '../components/PublicLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Briefcase, MapPin, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function CareersPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showApply, setShowApply] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', cover_letter: '' });

  useEffect(() => { loadJobs(); }, []);

  const loadJobs = async () => {
    try { const res = await api.get('/api/recruitment/jobs'); setJobs(res.data.jobs || []); }
    catch {} finally { setLoading(false); }
  };

  const applyForJob = async () => {
    if (!form.name || !form.email) { toast.error('Name and email required'); return; }
    try {
      await api.post('/api/recruitment/apply', { ...form, job_id: selectedJob?.id });
      toast.success('Application submitted!');
      setShowApply(false);
      setForm({ name: '', email: '', phone: '', cover_letter: '' });
    } catch { toast.error('Failed to submit'); }
  };

  return (
    <>
      <PublicHeader />
      <main className="bg-[#FBF7EF] min-h-screen">
        <section className="max-w-[1200px] mx-auto px-4 sm:px-6 py-10" data-testid="careers-page">
          <div className="text-center mb-12">
            <h1 className="news-masthead text-4xl font-bold">Join Our Newsroom</h1>
            <p className="text-[#5B616E] mt-3 max-w-lg mx-auto">Be part of the team that delivers breaking news and in-depth analysis to millions of readers worldwide.</p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{[1,2,3].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}</div>
          ) : jobs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {jobs.map(j => (
                <Card key={j.id} className="bg-white border-[#E7E0D6] news-card cursor-pointer" onClick={() => { setSelectedJob(j); setShowApply(true); }}>
                  <CardContent className="pt-6">
                    <Badge variant="outline" className="text-[10px] capitalize mb-2">{j.type?.replace('_', ' ')}</Badge>
                    <h3 className="text-lg font-semibold news-masthead">{j.title}</h3>
                    <div className="flex items-center gap-4 mt-3 text-xs text-[#5B616E]">
                      {j.department && <span>{j.department}</span>}
                      {j.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{j.location}</span>}
                    </div>
                    {j.salary_range && <p className="text-sm text-[#B89B5E] font-medium mt-3">{j.salary_range}</p>}
                    {j.description && <p className="text-xs text-[#5B616E] mt-3 line-clamp-3">{j.description}</p>}
                    <Button className="mt-4 bg-[#121316] text-white hover:bg-[#1B1D22] w-full"><Send className="w-4 h-4 mr-2" /> Apply Now</Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16"><Briefcase className="w-16 h-16 mx-auto mb-4 text-[#E7E0D6]" /><p className="text-[#5B616E]">No open positions at this time</p></div>
          )}
        </section>
      </main>

      <Dialog open={showApply} onOpenChange={setShowApply}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Apply for: {selectedJob?.title}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Full Name" />
            <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="Email" />
            <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="Phone (optional)" />
            <Textarea value={form.cover_letter} onChange={e => setForm({...form, cover_letter: e.target.value})} placeholder="Cover letter / Why you're a great fit..." rows={4} />
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setShowApply(false)}>Cancel</Button><Button onClick={applyForJob} className="bg-[#121316] text-white">Submit Application</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <PublicFooter />
    </>
  );
}
